﻿import React from 'react';
import { FormField } from '../inputs/FormField';
import { TextInput } from '../inputs/TextInput';
import { TextareaInput } from '../inputs/TextareaInput';
import { SelectInput, SelectOption } from '../inputs/SelectInput';
import { CheckboxInput } from '../inputs/CheckboxInput';
import { Courier } from './types';
import { CommonFieldProps } from './CommonFieldProps';

interface RetailerFieldsProps extends CommonFieldProps {
    localeOptions: SelectOption[];
    retailerCouriers: Courier[];
    handleRetailerCourierToggle: (id: string) => void;
}

export const RetailerFields: React.FC<RetailerFieldsProps> = ({ 
    formik, 
    getFieldError, 
    hasError, 
    localeOptions, 
    retailerCouriers, 
    handleRetailerCourierToggle 
}) => {
    const selectedLocaleOption = localeOptions.find(opt => opt.value === formik.values.retailerLocaleCode) || null;
    
    return (
        <div>
            <h2 className="h4 fw-bold mb-4 text-dark">Store Details</h2>
            
            <FormField
                label="Store Name"
                required
                htmlFor="retailerName"
                error={getFieldError('retailerName')}
            >
                <TextInput
                    id="retailerName"
                    name="retailerName"
                    value={formik.values.retailerName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="e.g. Amazon, ASOS, Zara"
                    required
                    hasError={hasError('retailerName')}
                />
            </FormField>

            <FormField
                label="Country / Region"
                required
                htmlFor="retailerLocaleCode"
                error={getFieldError('retailerLocaleCode')}
            >
                <SelectInput
                    inputId="retailerLocaleCode"
                    value={selectedLocaleOption}
                    onChange={(option: SelectOption | null) => formik.setFieldValue('retailerLocaleCode', option?.value || '')}
                    options={localeOptions}
                    placeholder="Where is this store based?"
                    isClearable
                    isSearchable
                    hasError={hasError('retailerLocaleCode')}
                />
            </FormField>

            <FormField
                label="Website URL"
                helpText="The store's website address (optional)"
                htmlFor="retailerUrl"
                error={getFieldError('retailerUrl')}
            >
                <TextInput
                    id="retailerUrl"
                    name="retailerUrl"
                    type="url"
                    value={formik.values.retailerUrl}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="https://example.com"
                    hasError={hasError('retailerUrl')}
                />
            </FormField>

            <FormField
                label="A bit about them"
                helpText="What do they sell? (optional)"
                htmlFor="retailerDescription"
                error={getFieldError('retailerDescription')}
            >
                <TextareaInput
                    id="retailerDescription"
                    name="retailerDescription"
                    value={formik.values.retailerDescription}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    rows={4}
                    placeholder="e.g. A clothing brand known for their sustainable fabrics..."
                    hasError={hasError('retailerDescription')}
                />
            </FormField>

            {formik.values.retailerLocaleCode && (
                <div className="mb-4">
                    <fieldset>
                        <legend className="form-label fw-bold">Who do they ship with?</legend>
                        <div className="form-text mb-3">Select the couriers you know they use (optional)</div>
                        {retailerCouriers.length > 0 ? (
                            <div className="border rounded-4 p-3 bg-light bg-opacity-50" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                {retailerCouriers.map(courier => (
                                    <CheckboxInput
                                        key={courier.id}
                                        id={`retailerCourier_${courier.id}`}
                                        name={`retailerCourier_${courier.id}`}
                                        checked={formik.values.selectedRetailerCourierIds.includes(courier.id)}
                                        onChange={() => handleRetailerCourierToggle(courier.id)}
                                        label={`${courier.name}${courier.localeName ? ` (${courier.localeName})` : ''}`}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="text-muted italic">No couriers listed for this country yet.</div>
                        )}
                    </fieldset>
                </div>
            )}
        </div>
    );
};
